// Pure fetch wrappers for the simple-checkout API surface. Used by both the
// SSR'd Next page (apiBase = '') and the standalone embed (apiBase = 'https://www.trend.at').
// MUST stay framework-agnostic.
import type {
  ContractResponse,
  Country,
  OfferingInfoResponse,
  PaymentMethod,
  Salutation,
  SimpleCreateResponse,
  VariantLookupResponse,
} from './types';

// Compax address-payload constants.
// Country numeric ids per Compax country lookup (matches what the shared
// fe-components lib also uses): AT=3, DE=1, CH=200.
// Salutation address IDs (different from the CustomerSalutation enum used by
// auth/customer mutations — these go into Address.salutation for billing/shipping).
export const COUNTRY_ID_BY_CODE: Record<Country, number> = {
  AT: 3,
  DE: 1,
  CH: 200,
};
export const SALUTATION_ID: Record<Salutation, number> = {
  HERR: 1,
  FRAU: 2,
  FIRMA: 5,
};

export async function lookupVariant(apiBase: string, variantId: string): Promise<VariantLookupResponse> {
  const res = await fetch(`${apiBase}/api/checkout/variant-to-offering`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ variant: variantId }),
  });
  return res.json();
}

// Resolve a Compax offering id back to its Statamic page metadata (currently
// just `title`/`marketingCode`) so the embed can show the configured page
// title in the modal header for the direct-offeringId flow.
export async function lookupOffering(apiBase: string, offeringId: string): Promise<OfferingInfoResponse> {
  const res = await fetch(`${apiBase}/api/checkout/offering-info`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ offeringId }),
  });
  return res.json();
}

// Re-priced response after a country switch. Same shape the embed already
// uses to drive the price line, minus the offering metadata (the modal
// already has it from the initial lookup).
export type RepriceResponse =
  | { success: true; price: number | null; currency: string; period: string | null }
  | { success: false; error: string };

// Calls /api/checkout/reprice to recompute the basket totals with the
// chosen shipping country. The basketId/uuid pair was returned by the
// initial offering-info / variant-to-offering lookup, so this reuses the
// same Compax basket — no extra basket gets created. accessToken must be
// supplied when the country is non-AT (Compax rejects 401 otherwise) — the
// embed obtains it via createPreviewCustomer the first time the buyer
// switches away from AT.
export async function reprice(
  apiBase: string,
  payload: { basketId: string; basketUuid: string; country: Country; accessToken?: string },
): Promise<RepriceResponse> {
  const res = await fetch(`${apiBase}/api/checkout/reprice`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json();
}

export type CreateBasketResponse =
  | {
      success: true;
      basketId: string;
      basketUuid: string;
      price: number | null;
      currency: string;
      period: string | null;
    }
  | { success: false; error: string };

// Lazily creates a Compax basket for an offering. The layover open path no
// longer spawns one (offering-info / variant-to-offering serve cached display
// data), so the embed calls this only when it needs a basket to reprice
// against — i.e. the first time the buyer switches delivery country away from
// AT. Returns the basketId/uuid pair to feed into `reprice`.
export async function createBasket(
  apiBase: string,
  offeringId: string,
  marketingCode: string,
): Promise<CreateBasketResponse> {
  const res = await fetch(`${apiBase}/api/checkout/create-basket`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ offeringId, marketingCode }),
  });
  return res.json();
}

export type ApplyPromoResponse =
  | { success: true; promotionalCode: string; discount: number }
  | { success: false; error: string };

// Checks a promo code against a throwaway basket; returns the discount to show.
// The order itself sends the code again with createContract.
export async function applyPromoCode(
  apiBase: string,
  payload: { offeringId: string; marketingCode: string; promotionalCode: string },
): Promise<ApplyPromoResponse> {
  const res = await fetch(`${apiBase}/api/checkout/apply-promo`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json();
}

export type PreviewCustomerResponse = { success: true; accessToken: string } | { success: false; error: string };

// Creates an anonymous Compax customer with placeholder name fields, just
// so we get a customer-scoped access_token. The embed uses this only when
// the buyer switches to DE/CH (those are the cases where shipping cost is
// different from the default productsOffering total and we need a fresh
// re-priced basket). The placeholder customer's name/email gets overwritten
// with the real values at contract submission. See the server endpoint
// docstring for the trade-off (one orphaned customer per DE/CH submit).
export async function createPreviewCustomer(
  apiBase: string,
  payload: { portal?: string } = {},
): Promise<PreviewCustomerResponse> {
  const res = await fetch(`${apiBase}/api/checkout/create-preview-customer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json();
}

export async function simpleCreate(
  apiBase: string,
  payload: {
    firstName: string;
    lastName: string;
    salutation: Salutation;
    // Address fields are passed up here so Compax can store mainAddress on the
    // anonymous customer — required for the subsequent userFromCustomer call
    // to match (it looks up by customerNumber + lastName + zip).
    postalCode: string;
    city: string;
    street?: string;
    houseNumber?: string;
    portal?: string;
    // Real email — when provided and not already taken in Compax, used as the
    // UUser login so the user can sign in with it after a password reset.
    // Falls back to a noreply-* placeholder if the email is already taken.
    email?: string;
    // Marketing-opt-in checkbox value — forwarded server-side as `terms`
    // on customerCreateAnonymous, which drives Compax's consent referenz.
    // Default false (DSGVO-safe) when omitted.
    marketing?: boolean;
  },
): Promise<SimpleCreateResponse> {
  const res = await fetch(`${apiBase}/api/checkout/simple-create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ portal: 'trend', ...payload }),
  });
  return res.json();
}

export async function createContract(
  apiBase: string,
  accessToken: string,
  payload: {
    offeringId: string;
    marketingCode: string;
    // Contact details — attached to the customer record as contactPersons[0]
    // inside customerBasket. Compax stitches them onto the (anonymous)
    // customer record at submission time.
    email: string;
    // Optional date of birth (ISO YYYY-MM-DD). Forwarded to Compax via
    // customerUpdateBasic at the end of the anonymous flow; omitted when
    // undefined/empty so we never blank a previously-set value.
    birthday?: string;
    firstName: string;
    lastName: string;
    // Optional; omitted from the Compax address when empty.
    companyName?: string;
    salutation: Salutation;
    street: string;
    houseNumber: string;
    zip: string;
    city: string;
    country: Country;
    // Optional separate billing address. When provided, it replaces the
    // shipping block as the contract's billingAddress; otherwise both
    // addresses are identical (shipping is the default for both).
    billing?: {
      salutation: Salutation;
      firstName: string;
      lastName: string;
      companyName?: string;
      street: string;
      houseNumber: string;
      zip: string;
      city: string;
      country: Country;
    };
    // Discriminator picked in the modal. INVOICE = no payment-rail metadata,
    // Compax bills the customer directly. STRIPE = paymentMethodId +
    // stripeCustomerId must be provided (caller already ran
    // confirmCardSetup on the Stripe Elements).
    paymentMethod: PaymentMethod;
    paymentMethodId?: string;
    // SEPA direct debit details, normalised (IBAN/BIC uppercase, no spaces).
    sepa?: { accountHolder: string; iban: string; bank: string; bic: string };
    stripeCustomerId?: string;
    // When true, trigger customerResetPassword(email) server-side after a
    // successful contract submission so the brand-new user gets a "set your
    // password" email. False for the existing-customer fallback (placeholder
    // UUser login) — that user already has working credentials.
    loginIsRealEmail: boolean;
    // Explicit marketing opt-in. Server adds the buyer to the Brevo
    // subscriber list only when true; defaults to false to satisfy DSGVO
    // affirmative-consent requirements.
    marketing: boolean;
    // Promo code checked beforehand via applyPromoCode; the order fails if Compax rejects it.
    promotionalCode?: string;
  },
): Promise<ContractResponse> {
  const shippingAddress = {
    firstName: payload.firstName,
    lastName: payload.lastName,
    ...(payload.companyName ? { companyName: payload.companyName } : {}),
    street: payload.street,
    houseNumber: payload.houseNumber,
    zip: payload.zip,
    city: payload.city,
    country: { id: COUNTRY_ID_BY_CODE[payload.country] },
    salutation: { id: SALUTATION_ID[payload.salutation] },
  };
  // Only emit a billingAddress when the buyer actually filled the
  // "Andere Rechnungsadresse" block. Otherwise leave it undefined so
  // Compax falls back to shippingAddress for invoicing — keeps the
  // customer record clean instead of writing a duplicate-of-shipping
  // row that the marketing team has to filter out downstream.
  const billingAddress = payload.billing
    ? {
        firstName: payload.billing.firstName,
        lastName: payload.billing.lastName,
        ...(payload.billing.companyName ? { companyName: payload.billing.companyName } : {}),
        street: payload.billing.street,
        houseNumber: payload.billing.houseNumber,
        zip: payload.billing.zip,
        city: payload.billing.city,
        country: { id: COUNTRY_ID_BY_CODE[payload.billing.country] },
        salutation: { id: SALUTATION_ID[payload.billing.salutation] },
      }
    : undefined;

  const paymentMethodBody =
    payload.paymentMethod === 'STRIPE'
      ? {
          type: 'STRIPE' as const,
          stripeAccount: {
            paymentMethodId: payload.paymentMethodId,
            stripeCustomerId: payload.stripeCustomerId,
          },
        }
      : payload.paymentMethod === 'SEPA' && payload.sepa
        ? { type: 'SEPA' as const, bankAccount: payload.sepa }
        : { type: 'INVOICE' as const };

  const res = await fetch(`${apiBase}/api/checkout/create-contract`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      // Varnish strips Authorization on www.trend.at; this header survives.
      'X-Checkout-Token': accessToken,
    },
    body: JSON.stringify({
      offeringId: payload.offeringId,
      marketingCode: payload.marketingCode,
      contactEmail: payload.email,
      contactBirthday: payload.birthday,
      contactFirstName: payload.firstName,
      contactLastName: payload.lastName,
      contactSalutation: payload.salutation,
      billingAddress,
      shippingAddress,
      paymentMethod: paymentMethodBody,
      loginIsRealEmail: payload.loginIsRealEmail,
      marketing: payload.marketing,
      promotionalCode: payload.promotionalCode,
    }),
  });
  return res.json();
}
